import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { api, errMsg, fmt } from "../lib/api";
import { Stepper } from "./shared";

const remaining = (l) => Math.max(0, Math.round((l.qty - (l.received_qty || 0)) * 10000) / 10000);

const money = (v) => `$${Number(v || 0).toFixed(4)}`;

function ConflictModal({ conflicts, res, setRes, busy, onCancel, onConfirm }) {
  const done = conflicts && conflicts.every((c) => res[c.item_id]);
  return (
    <Dialog open={!!conflicts} onOpenChange={() => {}}>
      <DialogContent className="bg-[#0F1623] border-2 border-[#A78BFA] w-[calc(100vw-1.5rem)] sm:max-w-xl [&>button]:hidden" data-testid="cost-conflict-modal"
        onEscapeKeyDown={(e) => e.preventDefault()} onPointerDownOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-[#C4B5FD]">Manual Cost Override Conflict</DialogTitle>
          <DialogDescription className="text-sm text-[#9CA3AF]">These items have a locked manual cost that differs from this PO's landed cost. Choose what to do before stock is received.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {(conflicts || []).map((c) => (
            <div key={c.item_id} data-testid={`conflict-row-${c.sku}`} className="p-4 rounded-md border border-[#1E293B] space-y-3">
              <div><div className="font-semibold">{c.name}</div><div className="font-mono text-[11px] text-[#6B7280]">{c.sku}</div></div>
              <div className="grid grid-cols-2 gap-2 font-mono">
                <div className="p-2 rounded bg-[#090D16]"><div className="text-[10px] text-[#6B7280]">CURRENT MANUAL COST</div><div data-testid={`conflict-current-${c.sku}`} className="font-bold text-[#C4B5FD]">{money(c.current_cost)}/{c.unit}</div></div>
                <div className="p-2 rounded bg-[#090D16]"><div className="text-[10px] text-[#6B7280]">NEW PO LANDED COST</div><div data-testid={`conflict-new-${c.sku}`} className="font-bold text-[#38BDF8]">{money(c.new_cost)}/{c.unit}</div></div>
              </div>
              <div className="grid sm:grid-cols-2 gap-2">
                {[["keep", "Keep Manual Override"], ["update", "Remove Override & Update Cost"]].map(([v, l]) => (
                  <button key={v} data-testid={`conflict-${v}-${c.sku}`} onClick={() => setRes({ ...res, [c.item_id]: v })}
                    className={`h-12 px-3 rounded-md border text-sm font-semibold transition-colors ${res[c.item_id] === v ? "bg-[#A78BFA] border-[#A78BFA] text-[#090D16]" : "border-[#334155] hover:border-[#A78BFA]"}`}>{l}</button>
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-2">
          <button data-testid="conflict-cancel-btn" onClick={onCancel} disabled={busy} className="h-12 rounded-md border border-[#334155] font-semibold">Back</button>
          <button data-testid="conflict-confirm-btn" onClick={onConfirm} disabled={!done || busy} className="h-12 rounded-md bg-[#10B981] text-[#090D16] font-bold disabled:opacity-40">{busy ? "RECEIVING…" : "Confirm & Receive"}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function ReceiveDialog({ po, open, onClose, onDone }) {
  const [qtys, setQtys] = useState({});
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [conflicts, setConflicts] = useState(null);
  const [res, setRes] = useState({});
  const open_lines = po.lines.filter((l) => remaining(l) > 0);

  useEffect(() => { if (open) { setQtys(Object.fromEntries(open_lines.map((l) => [l.item_id, remaining(l)]))); setNote(""); setConflicts(null); setRes({}); } }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const total = Object.values(qtys).reduce((a, b) => a + (Number(b) || 0), 0);
  const submit = async (resolutions = {}) => {
    setBusy(true);
    try {
      const r = await api.post(`/purchase-orders/${po.id}/receive`, { note, resolutions, lines: open_lines.map((l) => ({ item_id: l.item_id, qty: Number(qtys[l.item_id]) || 0 })) });
      toast.success(r.data.status === "received" ? `${po.po_number} fully received` : `${po.po_number} partially received — balance stays on order`);
      setConflicts(null); onDone(); onClose();
    } catch (e) {
      const c = e?.response?.status === 409 && e.response.data?.detail?.conflicts;
      if (c) { setConflicts(c); setRes({}); } else toast.error(errMsg(e));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0F1623] border-[#334155] max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="receive-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Receive {po.po_number}</DialogTitle>
          <div className="text-sm text-[#9CA3AF]">Enter what actually arrived. Anything short stays on order as backordered.</div>
        </DialogHeader>
        <div className="space-y-3">
          {open_lines.map((l) => {
            const rem = remaining(l);
            const q = Number(qtys[l.item_id]) || 0;
            return (
              <div key={l.item_id} data-testid={`receive-line-${l.sku}`} className="p-4 border border-[#1E293B] rounded-md">
                <div className="flex flex-wrap justify-between gap-2 mb-3">
                  <div><div className="font-semibold">{l.name}</div><div className="font-mono text-[11px] text-[#6B7280]">{l.sku}</div></div>
                  <div className="font-mono text-xs text-right text-[#9CA3AF]">
                    ORDERED {fmt(l.qty)} · RECEIVED {fmt(l.received_qty || 0)}<br />
                    <span className="text-[#F59E0B]">REMAINING {fmt(rem)} {l.unit}</span>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <Stepper value={q} onChange={(v) => setQtys({ ...qtys, [l.item_id]: Math.min(v, rem) })} min={0} step={l.order_unit_qty > 1 ? l.order_unit_qty : 1} testId={`receive-qty-${l.sku}`} />
                  <button data-testid={`receive-all-${l.sku}`} onClick={() => setQtys({ ...qtys, [l.item_id]: rem })} className="h-12 px-3 rounded-md border border-[#334155] text-xs font-bold">ALL</button>
                  <button data-testid={`receive-none-${l.sku}`} onClick={() => setQtys({ ...qtys, [l.item_id]: 0 })} className="h-12 px-3 rounded-md border border-[#334155] text-xs font-bold text-[#9CA3AF]">NONE</button>
                  <span className={`font-mono text-[10px] font-bold ${q >= rem ? "text-[#10B981]" : q > 0 ? "text-[#F59E0B]" : "text-[#6B7280]"}`}>
                    {q >= rem ? "FULLY RECEIVED" : q > 0 ? `BACKORDER ${fmt(rem - q)}` : "NOT IN THIS SHIPMENT"}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
        <input data-testid="receive-note-input" placeholder="Packing slip / note (optional)" value={note} onChange={(e) => setNote(e.target.value)}
          className="h-12 px-4 bg-[#090D16] border border-[#334155] rounded-md focus:outline-none focus:border-[#F59E0B]" />
        <button data-testid="receive-submit-btn" disabled={busy || total <= 0} onClick={() => submit()}
          className="h-14 rounded-md bg-[#10B981] text-[#090D16] font-display font-extrabold text-lg disabled:opacity-40">
          {busy ? "RECEIVING…" : "ADD TO STOCK"}
        </button>
      </DialogContent>
      <ConflictModal conflicts={conflicts} res={res} setRes={setRes} busy={busy} onCancel={() => setConflicts(null)} onConfirm={() => submit(res)} />
    </Dialog>
  );
}
